import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * `decodeURIComponent` throws `URIError` on a malformed percent-escape, and
 * `/page#%` is a URL anyone can type. Throwing out of the effect below would
 * take the route down, so an undecodable fragment resolves to no target and
 * the offset is left alone.
 */
export const fragmentId = (hash: string): string | null => {
  const raw = hash.slice(1);
  if (!raw) return null;

  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
};

/**
 * [MCV-1724] The one definition of where an anchor jump lands: the distance
 * from the top of the viewport at which `ScrollToTop` parks a hash target. The
 * FAQ scroll-spy reads the same number, so the section a jump lands on is
 * always the one the navigation highlights and the two can never disagree.
 */
export const anchorClearance = (target: Element): number => {
  // Defer to the target's own `scroll-margin-top` where it declares one. That
  // is what `scrollIntoView` honours, so matching it keeps this path in step
  // with in-page anchor clicks instead of quietly disagreeing with them — and
  // it picks up responsive clearance for free. FAQ sections, for one, reserve
  // more room on mobile than the header alone needs, because the sticky
  // "Jump to" navigator sits below it. [MCV-1514]
  const declared = parseFloat(getComputedStyle(target).scrollMarginTop);
  const headerHeight = document.querySelector('header')?.getBoundingClientRect().height ?? 0;

  // Header height is the floor, not the answer: a target that declares no
  // margin would otherwise land underneath the fixed header.
  return Math.max(Number.isFinite(declared) ? declared : 0, headerHeight);
};

/**
 * [MCV-1514] Puts the document at the right offset for the route being entered.
 *
 * `BrowserRouter` does no scroll restoration of its own, so a route change
 * keeps the previous page's offset. That was masked while `PublicHeader` was
 * `absolute`: the nav scrolled out of view with the page, so reaching a link
 * meant scrolling back to the top first, which reset the offset for free.
 * Making the header `fixed` keeps the links reachable at any offset, so
 * without this the next page loads mid-scroll with its heading behind the
 * header.
 *
 * `/page#section` is the same problem one level down. The browser only honours
 * a fragment on a full document load, not on a client-side route change, so a
 * hash link would otherwise inherit the previous page's offset too. We scroll
 * the target into view ourselves, offset by the fixed header so the anchor
 * isn't hidden underneath it. An unresolvable fragment leaves the offset alone
 * rather than guessing.
 *
 * Mount once inside the router, at each public shell (`PublicApp`, and the
 * `SignedOut` shell in `app.tsx`). The authenticated shell scrolls its own
 * content pane rather than the document and is unaffected.
 */
export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // jsdom defines `scrollTo` but throws "Not implemented" through the virtual
    // console; guarding keeps node-mode specs that render a shell quiet.
    if (typeof window.scrollTo !== 'function') return;

    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = fragmentId(hash);
    const target = id ? document.getElementById(id) : null;
    if (!target) return;

    const clearance = anchorClearance(target);
    const top = target.getBoundingClientRect().top + window.scrollY - clearance;

    window.scrollTo(0, Math.max(0, top));
  }, [pathname, hash]);

  return null;
};
